import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';

import { Box, Card } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import { useModal, useConfirmModal } from 'src/hooks/useModal';

import { genericApiToaster } from 'src/services/utils';
import { fetchDepartments } from 'src/services/department.service';
import {
  fetchAllTags,
  deleteMultipleTags,
  fetchTagsByDepartments,
} from 'src/services/tag.service';

import TableToolbar from 'src/components/ToolBars/TableToolbar';
import BannerPage from 'src/components/banner-page/banner-page';

import TagsTable from './table/tagsTable';
import AddUpdateTags from './modals/AddUpdateTags';
import SubFilterPopper from './popovers/SubFilterPopover';
import MainFilterPopper from './popovers/MainFilterPopover';

function TagsView() {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const { showToast, hideToast } = useToast();
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [page, setPage] = useState(0);

  // Aligned search and filter states
  const [searchValue, setSearchValue] = useState('');
  const [searchBy, setSearchBy] = useState('name'); // Default search target
  const [filterBy, setFilterBy] = useState(''); // Category filter type
  const [filterTitle, setFilterTitle] = useState(''); // Selected filter value

  const [isLoading, setIsLoading] = useState(false);
  const [filterPopover, setFilterPopover] = useState(null);

  const { departmentIds, permissions } = useAuth();
  const [departmentList, setDepartmentList] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('id');
  const [subFilterPopover, setSubFilterPopover] = useState({
    department: null,
  });

  const canCreate = permissions?.includes('TAG:CREATE');
  const isGlobal = !departmentIds || departmentIds.length === 0;

  const getDepartMents = async () => {
    genericApiToaster(
      fetchDepartments,
      [],
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      (res) => {
        const filtered = isGlobal
          ? res
          : res.filter((dep) => departmentIds.includes(dep.id));
        setDepartmentList(filtered);
      }
    );
  };

  useEffect(() => {
    getDepartMents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobal]);

  const handleFetchTags = async () => {
    setIsLoading(true);

    const apiCall = isGlobal ? fetchAllTags : fetchTagsByDepartments;
    const apiArgs = isGlobal ? [] : [departmentIds];

    genericApiToaster(
      apiCall,
      apiArgs,
      '',
      t('errorMessages.failedToFetchTags'),
      showToast,
      hideToast,
      t,
      setTags,
      undefined,
      () => setIsLoading(false)
    );
  };

  useEffect(() => {
    handleFetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentIds, isGlobal]);

  const multipleDeleteSuccessCallback = (res, selected) => {
    showToast({
      message: t('successMessages.deleteMultipleTagsSuccess', {
        tagsDeleted: res.deletedCount,
        totalTags: selected.length || 1,
      }),
      severity: 'success',
      props: { hideToast },
    });
    handleFetchTags();
    setSelectedTags([]);
    setPage(0);
  };

  const multipleDeleteErrorCallback = (err, selected) => {
    if (err.response?.data === 'Tag is related to tickets.') {
      showConfirmMultipleDeleteModal();
    } else {
      showToast({
        message: t('errorMessages.errorDeletingTags'),
        severity: 'error',
        props: { hideToast },
      });
    }
  };

  const multipleTagsDelete = async (selected, isConfirmedDelete = false) => {
    genericApiToaster(
      deleteMultipleTags,
      [selected, isConfirmedDelete],
      '',
      '',
      showToast,
      hideToast,
      t,
      (res) => multipleDeleteSuccessCallback(res, selected),
      (err) => multipleDeleteErrorCallback(err, selected)
    );
  };

  const { showConfirmModal: showConfirmMultipleDeleteModal } = useConfirmModal(
    t('pages.deletePage.confirmDelete'),
    `${t('pages.tagsPage.oneOrMoreTagsRelatedtoTicketsConfirmDeletion')}`,
    async () => {
      multipleTagsDelete(selectedTags, true);
    }
  );

  const handleOpenFilter = (event) => {
    setFilterPopover(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterPopover(null);
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setSearchValue(event.target.value);
  };

  const handleRemoveSelected = () => {
    setSelectedTags([]);
  };

  const handleCloseSubFilter = () => {
    setSubFilterPopover({
      department: null,
    });
  };

  return (
    <Box>
      <Box sx={!canCreate ? { '& button': { display: 'none !important' } } : {}}>
        <BannerPage
          title={t('nav.tags')}
          actionClick={() =>
            showModal({
              title: t('pages.tagsPage.addNewTag'),
              Component: AddUpdateTags,
              props: {
                hideModal,
                handleFetchTags,
                departmentList,
              },
            })
          }
          textButton={t('pages.tagsPage.addNewTag')}
          titleButton={t('pages.tagsPage.addNewTag')}
        />
      </Box>

      <Card>
        <TableToolbar
          numSelected={selectedTags.length}
          filterName={searchValue} // FIXED: Mapped to searchValue
          filterBy={searchBy} // Binds text-search key (id, name, description)
          setFilterBy={setSearchBy} // Binds the search field updater
          handleCloseFilter={handleCloseFilter}
          handleOpenFilter={handleOpenFilter}
          onFilterName={handleFilterByName}
          selected={selectedTags}
          handleRemoveSelected={handleRemoveSelected}
          deleteFunc={multipleTagsDelete}
          model={t(`nav.tags`)}
          setFilterName={setSearchValue}
          filterValue={filterTitle} // FIXED: Binds category filter chip display
          setFilterValue={setFilterTitle} // FIXED: Binds category filter chip clearing
          departmentsData={departmentList} // FIXED: Resolves numeric department IDs to names
          setPage={setPage}
        />

        <TagsTable
          tags={tags}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          isLoading={isLoading}
          searchValue={searchValue} // FIXED: Passes search text
          searchBy={searchBy} // FIXED: Passes search field
          filterBy={filterBy} // FIXED: Passes category type
          filterTitle={filterTitle} // FIXED: Passes category value
          handleFetchTags={handleFetchTags}
          departmentList={departmentList}
          page={page}
          setPage={setPage}
        />

        {/* main popper */}
        <MainFilterPopper
          filterPopover={filterPopover}
          setFilterName={setFilterTitle} // FIXED: Feeds directly into filterTitle
          setFilterBy={setFilterBy}
          setSearchBy={setSearchBy}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          handleCloseFilter={handleCloseFilter}
          handleCloseSubFilter={handleCloseSubFilter}
          setSubFilterPopover={setSubFilterPopover}
          setPage={setPage}
        />

        {/* sub popper */}
        <SubFilterPopper
          subFilterPopover={subFilterPopover}
          handleCloseSubFilter={handleCloseSubFilter}
          setFilterBy={setFilterBy}
          setSearchBy={setSearchBy}
          setSelectedFilter={setSelectedFilter}
          setFilterName={setFilterTitle} // FIXED: Feeds directly into filterTitle
          handleCloseFilter={handleCloseFilter}
          setPage={setPage}
          departmentData={departmentList}
        />
      </Card>
    </Box>
  );
}

export default TagsView;
